import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { useChatStore } from "@/hooks/useChatStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useBuildingDetail } from "@/hooks/buildings/useBuildingDetail";
import type { SidebarChatProps, ChatTextSize } from "./types";
import { ChatWidget } from "./ChatWidget";
import {
  COLLAPSED_HEIGHT,
  COLLAPSED_WIDTH,
  TOP_OFFSET,
  BOTTOM_MARGIN,
  NAV_COLLAPSED_WIDTH,
  NAV_EXPANDED_WIDTH,
  bottomRightPosition,
  clampToViewport,
} from "./chatWidgetLayout";
import { BottomRightLaneSlot } from "@/components/layout/BottomRightLane";
import { ChatNudge } from "./ChatNudge";
import { AttachmentProcessingNote } from "./AttachmentProcessingNote";
import { setNudgeLaneVisible } from "./chatNudgeStore";
import { useChatNudges } from "./useChatNudges";
import { useChatSessions } from "./useChatSessions";
import { useChatFeedback } from "./useChatFeedback";
import { useChatAskUser } from "./useChatAskUser";
import { useChatSend } from "./useChatSend";
import { useChatWindowInteractions } from "./useChatWindowInteractions";
import { useCollapsedReplyState } from "./useCollapsedReplyState";
import { SidebarChatHeader } from "./SidebarChatHeader";
import { SidebarChatMessages, type ChatMessage as DisplayChatMessage } from "./SidebarChatMessages";
import { SidebarChatInput } from "./SidebarChatInput";
import { DocumentPicker } from "@/components/documents/DocumentPicker";
import { WorkInProjectDialog } from "./WorkInProjectDialog";
import { FeedbackDialog } from "./FeedbackDialog";
import { useDocumentPreview } from "./DocLink";
import type { IssueChipData } from "./DocLink";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import { ChatbotUploadButton, type ChatbotUploadButtonHandle } from "./ChatbotUploadButton";
import { ChatbotFilesPanel } from "./ChatbotFilesPanel";
import { useChatbotAttachmentStateSync } from "@/hooks/useChatbotAttachmentStateSync";
import { useChatExampleQuestions } from "./useChatExampleQuestions";
import { attachDroppedDocuments } from "./attachDroppedDocuments";
import type { MoveDragSelection } from "@/domains/documents/utils/documentMoveDrag";
import { logger } from "@/utils/logger";

type StoreMessage = ReturnType<ReturnType<typeof useChatStore>["getMessages"]>[number];
type AskUserChoices = ReturnType<typeof useChatAskUser>["askUserChoices"];

function copyList<T>(items: { slice(): T[] } | null | undefined): T[] {
  if (!items) return [];
  return items.slice();
}

function copyGeneratedFiles(
  files: StoreMessage["generatedFiles"] | undefined
): NonNullable<DisplayChatMessage["generatedFiles"]> {
  return copyList(files).map((file) => ({
    id: file.id,
    filename: file.filename,
    mime: file.mime,
    sizeBytes: file.sizeBytes,
    dataB64: file.dataB64,
  }));
}

function choicesForMessage(
  message: StoreMessage,
  index: number,
  lastIndex: number,
  askUserChoices: AskUserChoices
): AskUserChoices {
  if (index !== lastIndex) return null;
  if (message.role !== "assistant") return null;
  return askUserChoices;
}

function formatMessageChoices(message: StoreMessage, askUserChoices: AskUserChoices) {
  if (askUserChoices) return askUserChoices;
  return {
    choices: copyList(message.choices),
    choiceQuestion: message.choiceQuestion ?? "",
  };
}

function formatMessage(message: StoreMessage, askUserChoices: AskUserChoices): DisplayChatMessage {
  const messageChoices = formatMessageChoices(message, askUserChoices);
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    sourceFilenames: copyList(message.sourceFilenames),
    generatedFiles: copyGeneratedFiles(message.generatedFiles),
    choices: messageChoices.choices,
    choiceQuestion: messageChoices.choiceQuestion,
    isError: Boolean(message.isError),
    usedDocumentTool: message.usedDocumentTool,
    ehrLink: message.ehrLink,
  };
}

/**
 * SidebarChat - Main container component for the draggable AI chat widget (Franklin).
 *
 * Manages two states via ChatStore.isCollapsed:
 * - collapsed: Draggable bar with quick input (CollapsedChatBar)
 * - expanded: Draggable & resizable panel with full chat UI
 *
 * isCollapsed is persisted in ChatStore to survive navigation between building pages.
 *
 * This file is the container: it wires the store to the panel's parts. The
 * behaviour lives in focused hooks alongside it — `useChatSessions`,
 * `useChatSend`, `useChatFeedback`, `useChatAskUser`, `useChatNudges`,
 * `useChatWindowInteractions`.
 */
export const SidebarChat = observer(function SidebarChat({
  buildingId,
  isSidebarCollapsed,
}: SidebarChatProps) {
  const { currentOrganization } = useOrganizationContext();
  const { userProfile } = useAuth();
  const chatStore = useChatStore();

  const { t } = useLanguage();
  const navigate = useNavigate();
  const orgId = currentOrganization?.id ?? null;
  const userIdentifier = userProfile?.id ? String(userProfile.id) : null;

  // Build EHR link from registry_code (same for all messages in this building)
  const { detail: building } = useBuildingDetail(buildingId);
  const registryCode = building?.base?.registry_code;
  const ehrLink = registryCode
    ? `https://livekluster.ehr.ee/ui/ehr/v1/building/${registryCode}`
    : null;

  // Document preview state for clickable document links
  const documentPreview = useDocumentPreview(buildingId);

  // Document picker state
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  // WHA-3829: "Work in a project" picker modal
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [showFilesPanel, setShowFilesPanel] = useState(false);
  const chatbotUploadRef = useRef<ChatbotUploadButtonHandle>(null);

  // WHA-3356: keep attached doc chip states in sync with documents.state as
  // the pipeline progresses. Drives the chip spinner / Send-enabled gating.
  useChatbotAttachmentStateSync();

  const feedback = useChatFeedback();

  const {
    sessions,
    activeSession,
    activeSessionId,
    sessionsLoading,
    threadId,
    isSessionBootstrapPending,
    setAnimatingTitle,
    handleSessionSelect,
    handleSessionCreate,
    handleSessionArchive,
    handleSessionRename,
    canRefreshChat,
    isRefreshingChat,
    handleRefreshChat,
  } = useChatSessions({
    buildingId,
    orgId,
    userIdentifier,
    onMessagesLoaded: feedback.hydrate,
    onMessagesReset: feedback.reset,
  });

  // Get messages and loading status from store (WHA-1981: per-thread state)
  const messages = threadId ? chatStore.getMessages(threadId) : [];
  const isLoading = threadId ? chatStore.getThreadIsLoading(threadId) : false;
  const statusMessage = threadId ? chatStore.getThreadStatusMessage(threadId) : null;
  const statusMeta = threadId ? chatStore.getThreadStatusMeta(threadId) : null;
  const intermediateOutput = threadId ? chatStore.getThreadIntermediateOutput(threadId) : null;
  const streamingDraft = threadId ? chatStore.getThreadStreamingDraft(threadId) : null;
  const {
    mode: collapsedMode,
    replyPreview,
    scrollToLatestReplyRequest,
    clearUnreadReply,
    openLatestReply,
  } = useCollapsedReplyState({
    messages,
    isLoading,
    isCollapsed: chatStore.isCollapsed,
    threadId,
  });

  const {
    pillDraft,
    setPillDraft,
    handleDraftChange,
    sendError,
    dismissSendError,
    handleSendMessage,
    handleStopStream,
    handleCancelQueued,
    handleEditLastMessage,
  } = useChatSend({
    buildingId,
    orgId,
    userIdentifier,
    threadId,
    isSessionBootstrapPending,
    onTitleGenerated: setAnimatingTitle,
  });

  const {
    pendingChoice,
    customAnswer,
    askUserChoices,
    handleChoiceSelect,
    handleCustomAnswerChange,
    handleChoiceConfirm,
    handleChoiceCancel,
    toolConfirmation,
    toolConfirmationSubmission,
    handleToolConfirmationApprove,
    handleToolConfirmationDeny,
    handleToolConfirmationDismiss,
  } = useChatAskUser({
    threadId,
    isLoading,
    conversationKey: `${activeSessionId ?? ""}:${messages.length}:${messages[messages.length - 1]?.id ?? ""}`,
    onSend: handleSendMessage,
  });
  const askUserActive = chatStore.pendingAskUser != null;

  // Toggle collapsed state — repositions so the pill lands back in its corner
  const handleToggleCollapse = useCallback(() => {
    if (chatStore.isCollapsed) {
      clearUnreadReply();
    }
    if (!chatStore.isCollapsed) {
      // The panel's input dies with the panel, so the pill draft it adopted
      // must die too — otherwise the next plain expand resurrects stale text.
      setPillDraft("");
      if (chatStore.isExpanded) {
        // Collapsing from expanded/fullscreen: back to the bottom-right corner,
        // at the width the panel had before it went fullscreen (setCollapsed
        // restores that size straight after this).
        chatStore.setWidgetPosition(bottomRightPosition(COLLAPSED_WIDTH));
      } else {
        // Collapsing from normal: keep the panel's bottom-right corner, so the
        // narrower pill appears against the panel's lower-right edge. Clamped,
        // because a panel taller than the viewport (or one left low by an
        // earlier window resize) would otherwise put the pill below the
        // bottom edge, where the overflow-hidden portal clips it away and the
        // user can never click it again.
        const expandedBottom = chatStore.widgetPosition.y + chatStore.widgetSize.height;
        chatStore.setWidgetPosition(
          clampToViewport(
            {
              x: chatStore.widgetPosition.x + chatStore.widgetSize.width - COLLAPSED_WIDTH,
              y: expandedBottom - COLLAPSED_HEIGHT,
            },
            COLLAPSED_WIDTH,
            COLLAPSED_HEIGHT
          )
        );
      }
    }
    chatStore.setCollapsed(!chatStore.isCollapsed);
  }, [chatStore, clearUnreadReply, setPillDraft]);

  // getMessages returns a fresh snapshot so MobX message mutations stay reactive.
  // Derive the display shape directly instead of memoizing against that snapshot.
  const lastMessageIndex = messages.length - 1;
  const formattedMessages = messages.map((message, index) =>
    formatMessage(message, choicesForMessage(message, index, lastMessageIndex, askUserChoices))
  );

  const textSize: ChatTextSize = chatStore.widgetSize.width >= 360 ? "lg" : "sm";
  // Handle document link clicks - open preview dialog
  const handleDocLinkClick = useCallback(
    async (filename: string) => {
      await documentPreview.openPreview(filename);
    },
    [documentPreview]
  );

  // WHA-3469: clicking an attached-issue chip navigates to the issue page
  // and expands that issue — the same deep-link the documents page uses
  // (`?selected=` is consumed by PMIssues → issuesStore.selectIssue). The
  // chat is building-scoped, so `buildingId` is the correct target.
  const handleIssueClick = useCallback(
    (issue: IssueChipData) => {
      navigate(`/buildings/${buildingId}/issues?selected=${issue.id}`);
    },
    [navigate, buildingId]
  );

  // Show error toast when document preview fails
  const { error: previewError, clearError: clearPreviewError } = documentPreview;
  useEffect(() => {
    if (previewError) {
      toast.error("Cannot open document", { description: previewError });
      clearPreviewError();
    }
  }, [previewError, clearPreviewError]);
  // Expand to maximized mode (WHA-2390). Pass live nav-sidebar width so the
  // centering math lines up with whatever sidebar state is currently shown
  // (WHA-3109).
  const handleExpandToMax = useCallback(() => {
    chatStore.setExpanded(isSidebarCollapsed ? NAV_COLLAPSED_WIDTH : NAV_EXPANDED_WIDTH);
  }, [chatStore, isSidebarCollapsed]);

  // Restore from maximized to normal (WHA-2390)
  const handleCollapseFromMax = useCallback(() => {
    chatStore.setNormalSize();
  }, [chatStore]);

  // Expand callback for the pill — the panel opens in place above it, with
  // both right edges aligned so it grows upward out of the pill.
  const handleExpand = useCallback(() => {
    clearUnreadReply();
    const collapsedBottom = chatStore.widgetPosition.y + COLLAPSED_HEIGHT;
    const expandedHeight = chatStore.widgetSize.height;
    const availableHeight = window.innerHeight - TOP_OFFSET;
    let newY = collapsedBottom - expandedHeight;
    // Clamp: don't extend below viewport
    newY = Math.min(newY, availableHeight - expandedHeight - BOTTOM_MARGIN);
    // Clamp: don't go above portal top
    newY = Math.max(0, newY);
    // Keep the panel's right edge aligned with the narrower pill it grows out of.
    const newX = Math.max(
      0,
      chatStore.widgetPosition.x + COLLAPSED_WIDTH - chatStore.widgetSize.width
    );
    chatStore.setWidgetPosition({ x: newX, y: newY });
    chatStore.setCollapsed(false);
  }, [chatStore, clearUnreadReply]);

  // Typing in the pill opens the panel and carries the text into its input.
  const handleExpandWithDraft = useCallback(
    (draft: string) => {
      setPillDraft(draft);
      handleExpand();
    },
    [handleExpand, setPillDraft]
  );

  const handleOpenReply = useCallback(() => {
    openLatestReply();
    handleExpand();
  }, [handleExpand, openLatestReply]);

  // Proactive nudges above the pill — every one wired to a real event source
  // (upload queue, document pipeline state, issues created in the last 24h).
  // The new-issues count follows the chat's project scope so the number and
  // the "View issues" navigation agree with the context the user is in.
  const activeProject = chatStore.getProjectForBuilding(buildingId);
  const {
    nudge,
    dismiss: dismissNudge,
    clear: clearNudge,
  } = useChatNudges(buildingId, activeProject?.id ?? null);

  // The collapsed pill's lane is the nudge's display surface. While the panel
  // is open the store queues arrivals instead of displaying them, so a nudge's
  // once-per-session slot is only spent on a render the user can actually see.
  const isChatCollapsed = chatStore.isCollapsed;
  useEffect(() => {
    setNudgeLaneVisible(isChatCollapsed);
  }, [isChatCollapsed]);

  const handleNudgeSummarise = useCallback(() => {
    if (!nudge) return;
    const copy = t.documents.chatSidebar.nudges;
    const prompt =
      nudge.type === "processing_done"
        ? copy.summariseDocumentsPrompt.replace("{count}", String(nudge.count))
        : nudge.type === "new_issues"
          ? copy.summariseIssuesPrompt.replace("{count}", String(nudge.count))
          : null;
    if (!prompt) return;
    clearNudge();
    handleExpand();
    handleSendMessage(prompt);
  }, [nudge, t, clearNudge, handleExpand, handleSendMessage]);

  const handleNudgeViewIssues = useCallback(() => {
    clearNudge();
    navigate(
      activeProject
        ? `/buildings/${buildingId}/issues?project=${activeProject.id}`
        : `/buildings/${buildingId}/issues`
    );
  }, [clearNudge, navigate, buildingId, activeProject]);

  // Contextual example questions for the empty state — scoped to the active
  // project when there is one, else the building. The questions are chosen from
  // the real aggregate state of that scope (see `chatSuggestions.ts`).
  const scopeName = activeProject?.name ?? building?.base?.name ?? null;
  // Rotates the greeting and the wording of each suggestion. The building's chat
  // count is stable while a chat is open and moves on as new ones are started,
  // so the empty state stays put but does not repeat itself forever.
  const copyVariantSeed = sessions.length;
  const { questions: exampleQuestions } = useChatExampleQuestions({
    buildingId,
    projectId: activeProject?.id,
    scopeName: scopeName ?? t.documents.chatSidebar.scopeFallback,
    copy: t.documents.chatSidebar.examples,
    seed: copyVariantSeed,
    // Only the empty state shows them, and only an open panel shows the empty
    // state — the collapsed pill is mounted on every building page.
    enabled: !chatStore.isCollapsed && formattedMessages.length === 0,
  });

  // Documents dragged out of the documents list attach as they are — the same
  // chips the "Add from Franklin" picker creates.
  const handleDocumentsDropped = useCallback(
    (payload: MoveDragSelection) => {
      const copy = t.documents.chatSidebar.drop;
      void attachDroppedDocuments({
        payload,
        buildingId,
        alreadyAttachedIds: chatStore.attachedDocuments.map((d) => d.id),
        attach: (doc) => chatStore.addAttachedDocument(doc),
      })
        .then(({ skippedFolders, missing }) => {
          if (skippedFolders) toast.info(copy.foldersNotAttached);
          if (missing > 0) toast.warning(copy.someDocumentsNotAttached);
        })
        .catch((error: unknown) => {
          void logger.error(
            "ui",
            "chat-dropped-documents-attach-failed",
            "Failed to attach dropped documents",
            error instanceof Error ? error : new Error(String(error)),
            { buildingId }
          );
          toast.error(copy.attachFailed);
        });
    },
    [buildingId, chatStore, t]
  );

  const { dragKind, dropZoneProps, collapsedDropProps } = useChatWindowInteractions({
    isCollapsed: chatStore.isCollapsed,
    onExpand: handleExpand,
    onToggleCollapse: handleToggleCollapse,
    onFilesDropped: (files) => void chatbotUploadRef.current?.uploadFiles(files),
    onDocumentsDropped: handleDocumentsDropped,
  });

  // Chat content for expanded (non-collapsed) state
  const chatContent = (
    <div className="relative flex h-full w-full flex-col" {...dropZoneProps}>
      <SidebarChatHeader
        onClose={handleToggleCollapse}
        isExpanded={chatStore.isExpanded}
        onExpand={handleExpandToMax}
        onCollapse={handleCollapseFromMax}
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSessionSelect={handleSessionSelect}
        onSessionCreate={handleSessionCreate}
        onSessionArchive={handleSessionArchive}
        onSessionRename={handleSessionRename}
        sessionsLoading={sessionsLoading}
      />
      <SidebarChatMessages
        messages={formattedMessages}
        isLoading={isLoading}
        buildingId={buildingId}
        textSize={textSize}
        sessionsLoading={isSessionBootstrapPending || isRefreshingChat}
        ehrLink={ehrLink}
        onDocLinkClick={handleDocLinkClick}
        onIssueClick={handleIssueClick}
        onMessageFeedback={feedback.onMessageFeedback}
        messageFeedback={feedback.messageFeedback}
        feedbackLoadingId={feedback.feedbackLoadingId}
        statusMessage={statusMessage}
        statusMeta={statusMeta}
        intermediateOutput={intermediateOutput}
        streamingDraft={streamingDraft}
        onChoiceSelect={handleChoiceSelect}
        pendingChoice={pendingChoice}
        onChoiceConfirm={handleChoiceConfirm}
        onChoiceCancel={handleChoiceCancel}
        onRefreshChat={canRefreshChat && !isRefreshingChat ? handleRefreshChat : undefined}
        refreshChatLabel={t.buildings.chat.refreshChat}
        firstName={userProfile?.first_name}
        copyVariantSeed={copyVariantSeed}
        queuedMessages={chatStore.getPendingSends(threadId)}
        onCancelQueued={handleCancelQueued}
        exampleQuestions={exampleQuestions}
        onExampleSelect={(question) => void handleSendMessage(question, true)}
        onEditLastMessage={handleEditLastMessage}
        askUserChoices={askUserChoices?.choices ?? null}
        askUserQuestion={askUserChoices?.choiceQuestion ?? null}
        customAnswer={customAnswer}
        onCustomAnswerChange={handleCustomAnswerChange}
        toolConfirmation={toolConfirmation}
        toolConfirmationSubmission={toolConfirmationSubmission}
        onToolConfirmationApprove={handleToolConfirmationApprove}
        onToolConfirmationDeny={handleToolConfirmationDeny}
        onToolConfirmationDismiss={handleToolConfirmationDismiss}
        loadingDocIds={new Set(chatStore.loadingDocIds)}
        scrollToLatestReplyRequest={scrollToLatestReplyRequest}
      />
      <AttachmentProcessingNote messages={messages} loadingDocIds={chatStore.loadingDocIds} />
      <SidebarChatInput
        onSend={handleSendMessage}
        value={pillDraft}
        onValueChange={handleDraftChange}
        sendError={sendError}
        onDismissSendError={dismissSendError}
        isLoading={isLoading}
        disabled={isSessionBootstrapPending}
        onStop={handleStopStream}
        askUserActive={askUserActive}
        textSize={textSize}
        attachedDocuments={chatStore.attachedDocuments.slice()}
        onRemoveDocument={(id) => chatStore.removeAttachedDocument(id)}
        attachedIssues={chatStore.attachedIssues.slice()}
        onRemoveIssue={(id) => chatStore.removeAttachedIssue(id)}
        onIssueClick={handleIssueClick}
        onBrowseProjectsClick={() => setShowDocumentPicker(true)}
        onUploadClick={() => chatbotUploadRef.current?.openFilePicker()}
        onWorkInProjectClick={() => setShowProjectPicker(true)}
        onOpenFilesPanel={() => setShowFilesPanel(true)}
        activeProject={chatStore.getProjectForBuilding(buildingId)}
        onRemoveProject={() => chatStore.setProjectForBuilding(buildingId, null)}
        showDisclaimer={!chatStore.isCollapsed}
        {...(!chatStore.isCollapsed && {
          // Show the toggle even before a session exists (first chat); the
          // building-scoped accessors resolve the active session when present
          // and otherwise a pending preference seeded into the first session.
          webSearchEnabled: chatStore.getWebSearchForBuilding(buildingId),
          onWebSearchToggle: (enabled: boolean) =>
            chatStore.setWebSearchForBuilding(buildingId, enabled),
        })}
      />
      {showProjectPicker && (
        <WorkInProjectDialog
          isOpen={showProjectPicker}
          onClose={() => setShowProjectPicker(false)}
          buildingId={buildingId}
          selectedProjectId={chatStore.getProjectForBuilding(buildingId)?.id ?? null}
          onSelect={(project) =>
            chatStore.setProjectForBuilding(buildingId, { id: project.id, name: project.name })
          }
        />
      )}
      {showDocumentPicker && (
        <DocumentPicker
          open={showDocumentPicker}
          onOpenChange={setShowDocumentPicker}
          buildingId={buildingId}
          alreadyAttachedIds={chatStore.attachedDocuments.map((d) => d.id)}
          mode="multi"
          onSelect={(docs) => {
            docs.forEach((d) => chatStore.addAttachedDocument({ id: d.id, filename: d.filename }));
            setShowDocumentPicker(false);
          }}
        />
      )}
      <ChatbotUploadButton ref={chatbotUploadRef} buildingId={buildingId} />
      <ChatbotFilesPanel
        buildingId={buildingId}
        open={showFilesPanel}
        onClose={() => setShowFilesPanel(false)}
        onPreview={documentPreview.openPreviewByDoc}
      />
      <FeedbackDialog
        open={feedback.feedbackDialog.open}
        comment={feedback.feedbackComment}
        isSubmitting={
          feedback.feedbackDialog.messageId
            ? feedback.feedbackLoadingId === feedback.feedbackDialog.messageId
            : false
        }
        onCommentChange={feedback.setFeedbackComment}
        onSubmit={feedback.onDialogSubmit}
        onSkip={feedback.onDialogSkip}
        onOpenChange={feedback.onDialogOpenChange}
      />
      {dragKind && (
        <div
          className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-[10px] border-2 border-dashed border-primary bg-primary/10"
          aria-hidden="true"
        >
          <p className="px-6 text-center text-sm font-medium text-primary">
            {dragKind === "documents"
              ? t.documents.chatSidebar.drop.attachDocuments
              : t.documents.chatSidebar.drop.attachFiles}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <ChatWidget
        isCollapsed={chatStore.isCollapsed}
        isExpanded={chatStore.isExpanded}
        collapsedMode={collapsedMode}
        replyPreview={replyPreview || t.documents.chatSidebar.openLatestReply}
        onExpand={handleExpand}
        onExpandWithDraft={handleExpandWithDraft}
        onOpenReply={handleOpenReply}
        collapsedDropProps={collapsedDropProps}
        isCollapsedDropTarget={chatStore.isCollapsed && dragKind !== null}
      >
        {chatContent}
      </ChatWidget>
      {/* Only while the pill is collapsed: an open panel covers the lane (it owns
          the top of the corner). The store queues nudges while the panel is open
          (`setNudgeLaneVisible` above), so one only becomes current — and spends
          its once-per-session slot — when the lane is on screen; this gate covers
          the one-render window before that effect syncs. */}
      {nudge && chatStore.isCollapsed && (
        <BottomRightLaneSlot name="chatNudge">
          <ChatNudge
            nudge={nudge}
            onSummarise={handleNudgeSummarise}
            onViewIssues={nudge.type === "new_issues" ? handleNudgeViewIssues : undefined}
            onDismiss={dismissNudge}
          />
        </BottomRightLaneSlot>
      )}
      <DocumentViewerDialog
        document={documentPreview.document}
        blobUrl={documentPreview.blobUrl}
        blobData={documentPreview.blobData}
        open={documentPreview.isOpen}
        onOpenChange={(open) => {
          if (!open) documentPreview.closePreview();
        }}
        originalDwgFilePath={documentPreview.originalDwgFilePath ?? undefined}
      />
    </>
  );
});

SidebarChat.displayName = "SidebarChat";

export type { SidebarChatProps } from "./types";
export type { ViewMode } from "@/stores/chat/ChatStore";
